import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, patchRound } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Pencil, Users, ListNumbers, ChartBar, Timer, Golf, Lock, LockOpen, CaretDown } from '@phosphor-icons/react';
import { TEE_GAP_OPTIONS, isShotgunRound, isSplitTeeRound, playingHandicap, startHoleForGroup, formatPlayingHandicap, getEffectivePH, getAllowedPH, countingScoresOnLongHoles, teeTimeForGroup } from '../golf';
import { toast } from 'sonner';
import { effectiveCourseForPlayer } from '../lib/effectiveCourse';
import { roundFormat, effectiveTeamScoring, roundHasScores } from '../lib/formatHelpers';
import { computeTeamPH, defaultScrambleConfig, defaultMinDrives } from '../lib/scramble';
import { groupDisplayName } from '../lib/groupOrder';
import { setRoundUnlockToken } from '../lib/ownerAuth';
import { getTripAccess } from '../lib/tripAccess';
import { RoleGated } from '../components/RoleGated';
import OwnerPasswordDialog from '../components/OwnerPasswordDialog';
import OrganiserScoreCorrection from '../components/OrganiserScoreCorrection';

export default function RoundSetup() {
  const { code, roundId } = useParams();
  const navigate = useNavigate();
  const cacheKey = `trip_${code}`;
  const [trip, setTrip] = useState(() => {
    try { const c = sessionStorage.getItem(cacheKey); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [round, setRound] = useState(() => trip?.rounds?.find(r => r.id === roundId) || null);
  const [loading, setLoading] = useState(!trip || !round);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState('');
  const [revealHole, setRevealHole] = useState(15);
  const [freezeEnabled, setFreezeEnabled] = useState(true);
  const [startType, setStartType] = useState('standard');
  const [startingHole, setStartingHole] = useState(1);
  const [firstTeeTime, setFirstTeeTime] = useState('');
  const [teeGap, setTeeGap] = useState(8);
  const [teeGapRaw, setTeeGapRaw] = useState('8');
  const [teeGapError, setTeeGapError] = useState('');
  const teeGapErrorTimer = useRef(null);
  const [isFinal, setIsFinal] = useState(false);
  const [formatNote, setFormatNote] = useState('');
  const [splitTeeHoles, setSplitTeeHoles] = useState([1, 10]);
  const [splitTeeTimes, setSplitTeeTimes] = useState(['', '']);
  const [ownerUnlocked, setOwnerUnlocked] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);

  const showTeeGapError = (msg) => {
    clearTimeout(teeGapErrorTimer.current);
    setTeeGapError(msg);
    teeGapErrorTimer.current = setTimeout(() => setTeeGapError(''), 3000);
  };
  const clearTeeGapError = () => { clearTimeout(teeGapErrorTimer.current); setTeeGapError(''); };

  const initFormFromRound = useCallback((r) => {
    setLabel(r.label || '');
    setRevealHole(r.reveal_hole ?? 15);
    setFreezeEnabled(r.freeze_enabled !== false);
    setStartType(r.start_type || 'standard');
    setStartingHole(r.starting_hole || 1);
    setFirstTeeTime(r.first_tee_time || '');
    setTeeGap(r.tee_gap_minutes ?? 8);
    setTeeGapRaw(String(r.tee_gap_minutes ?? 8));
    setIsFinal(r.is_final || false);
    if (r.split_tee_holes?.length === 2) setSplitTeeHoles(r.split_tee_holes);
    if (r.split_tee_first_times?.length === 2) setSplitTeeTimes(r.split_tee_first_times.map(t => t || ''));
  }, []);

  // Init form from cached round if available
  const formInitRef = useRef(false);
  useEffect(() => {
    if (round && !formInitRef.current) { formInitRef.current = true; initFormFromRound(round); }
  }, [round, initFormFromRound]);

  const load = useCallback(async () => {
    try {
      const res = await getTrip(code);
      const data = res.data;
      setTrip(data);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(data)); } catch {}
      const r = data.rounds?.find(r => r.id === roundId);
      if (r) {
        setRound(r);
        initFormFromRound(r);
      }
    } catch {} finally { setLoading(false); }
  }, [code, roundId, cacheKey, initFormFromRound]);
  useEffect(() => { load(); }, [load]);

  const save = async (fields) => {
    setSaving(true);
    try {
      const res = await patchRound(code, roundId, fields);
      setTrip(res.data);
      const r = res.data.rounds?.find(r => r.id === roundId);
      if (r) setRound(r);
    } catch {} finally { setSaving(false); }
  };

  const [settingsOpen, setSettingsOpen] = useState(true);
  const settingsInitRef = useRef(false);

  // Collapse settings when scores exist (after first data load)
  useEffect(() => {
    if (round && !settingsInitRef.current) {
      settingsInitRef.current = true;
      if (roundHasScores(round)) setSettingsOpen(false);
    }
  }, [round]);

  if (loading) return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="round-setup-skeleton">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <div className="w-5 h-5 rounded bg-white/20" />
        <div className="flex-1">
          <div className="h-5 w-32 bg-white/20 rounded mb-1" />
          <div className="h-3 w-48 bg-white/10 rounded" />
        </div>
      </div>
      <div className="px-4 py-4 space-y-3">
        <div className="bg-white rounded-xl border border-[#E1E6E4] p-4 space-y-3">
          <div className="h-4 w-40 bg-[#E1E6E4] rounded animate-pulse" />
          <div className="h-10 w-full bg-[#F4F6F5] rounded-lg animate-pulse" />
          <div className="h-4 w-32 bg-[#E1E6E4] rounded animate-pulse" />
          <div className="h-10 w-full bg-[#F4F6F5] rounded-lg animate-pulse" />
        </div>
        <div className="bg-white rounded-xl border border-[#E1E6E4] p-4 space-y-3">
          <div className="h-4 w-36 bg-[#E1E6E4] rounded animate-pulse" />
          <div className="h-12 w-full bg-[#F4F6F5] rounded-lg animate-pulse" />
          <div className="h-12 w-full bg-[#F4F6F5] rounded-lg animate-pulse" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="h-12 bg-white rounded-xl border border-[#E1E6E4] animate-pulse" />
          <div className="h-12 bg-white rounded-xl border border-[#E1E6E4] animate-pulse" />
          <div className="h-12 bg-white rounded-xl border border-[#E1E6E4] animate-pulse" />
        </div>
      </div>
    </div>
  );
  if (!round) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><p className="text-[#D32F2F]">Round not found</p></div>;

  const hasScores = roundHasScores(round);
  const scoredLocked = hasScores && !ownerUnlocked;
  const players = trip?.players || [];
  const courseTees = round.course?.tees || [];
  const playerTees = round.player_tees || {};
  const access = getTripAccess(code);
  const role = access?.role || (access?.legacy ? 'organiser' : null);
  const isOrganiser = role === 'organiser';
  const locked = scoredLocked || !isOrganiser;
  const drawLocked = round.teams_locked === true;
  const scoringDisabled = !drawLocked && !hasScores;
  const teams = round.teams || [];
  const groupFinished = round.group_finished || [];
  const allGroupsFinished = teams.length > 0 && teams.every((_, i) => groupFinished[i] === true);
  const roundFinished = round.revealed === true || (hasScores && allGroupsFinished);

  const handlePlayerTeeChange = (playerId, teeName) => {
    const updated = { ...playerTees };
    if (!teeName || teeName === '__default__') {
      delete updated[playerId];
    } else {
      updated[playerId] = teeName;
    }
    save({ player_tees: updated });
  };

  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="round-setup">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(`/trip/${code}`)} className="text-white p-1" data-testid="back-btn"><ArrowLeft className="w-5 h-5" weight="bold" /></button>
        <div className="flex-1"><h1 className="text-xl text-white font-bold">{round.label}</h1><p className="text-xs text-[#B8C7C0]">{round.course?.name}</p></div>
      </div>

      <div className="px-4 py-6 max-w-lg mx-auto space-y-4 animate-fade-in-up">

        {/* ── Action buttons — always at top ── */}
        <div className="space-y-2" data-testid="round-actions">
          <Button onClick={() => {
            if (scoringDisabled) return;
            if (roundFinished && !isOrganiser) {
              navigate(`/trip/${code}/round/${roundId}/scorecard`);
              return;
            }
            if (!isOrganiser && trip?.pin) navigate(`/trip/${code}/round/${roundId}/pin`);
            else navigate(`/trip/${code}/round/${roundId}/scoring`);
          }}
            disabled={scoringDisabled}
            className={`w-full h-14 rounded-xl text-base font-semibold ${
              scoringDisabled
                ? 'bg-[#E1E6E4] text-[#B8C7C0] cursor-not-allowed'
                : 'bg-[#1D242B] hover:bg-[#2C3742] text-white'
            }`} data-testid="start-scoring-btn">
            <ListNumbers className="w-5 h-5 mr-2" weight="bold" /> {roundFinished ? (isOrganiser ? 'Review Scores' : 'View Scores') : hasScores ? 'Continue Scoring' : 'Start Scoring'}
          </Button>
          {roundFinished && (
            <p className="text-xs text-[#8A9BA3] text-center" data-testid="round-finished-msg">Round finished</p>
          )}
          {scoringDisabled && (
            <p className="text-xs text-[#8A9BA3] text-center" data-testid="scoring-disabled-msg">
              {isOrganiser ? (
                <button onClick={() => navigate(`/trip/${code}/round/${roundId}/teams`)}
                  className="text-[#1B4332] font-semibold underline underline-offset-2"
                  data-testid="lock-draw-link">
                  Lock the draw to open scoring
                </button>
              ) : 'Scoring opens once the organiser locks the draw'}
            </p>
          )}
          <div className="flex gap-2">
            <Button onClick={() => navigate(`/trip/${code}/round/${roundId}/leaderboard`)} variant="outline"
              className="flex-1 h-12 rounded-xl border-[#E1E6E4] text-[#1D242B] text-sm font-semibold" data-testid="view-leaderboard-btn">
              <ChartBar className="w-4 h-4 mr-1.5 text-[#E59000]" weight="fill" /> Leaderboard
            </Button>
            <Button onClick={() => navigate(`/trip/${code}/round/${roundId}/scorecard`)} variant="outline"
              className="flex-1 h-12 rounded-xl border-[#E1E6E4] text-[#1D242B] text-sm font-semibold" data-testid="view-scorecard-btn">
              <Golf className="w-4 h-4 mr-1.5 text-[#1B4332]" weight="fill" /> Scorecard
            </Button>
          </div>
          <Button onClick={() => navigate(`/trip/${code}/round/${roundId}/tee-sheet`)} variant="outline"
            className="w-full h-12 rounded-xl border-[#E1E6E4] text-[#1D242B] text-sm font-semibold" data-testid="view-tee-sheet-btn">
            <Users className="w-4 h-4 mr-1.5 text-[#1B4332]" weight="fill" /> Teams & Tee Times
          </Button>
        </div>

        {/* ── Organiser score correction — only when Master password unlocked ── */}
        {ownerUnlocked && round?.teams?.length > 0 && (
          <OrganiserScoreCorrection
            trip={trip} round={round} code={code} roundId={roundId}
            onRefresh={() => getTrip(code).then(r => {
              const rd = r.data?.rounds?.find(rd => rd.id === roundId);
              if (rd) setRound(rd);
              setTrip(r.data);
            }).catch(() => {})}
          />
        )}

        {/* ── Collapsible Round settings ── */}
        <button onClick={() => setSettingsOpen(!settingsOpen)}
          className="w-full flex items-center justify-between bg-white border border-[#E1E6E4] rounded-xl px-4 py-3 hover:bg-[#F4F6F5] transition-colors"
          data-testid="round-settings-toggle">
          <span className="text-sm font-semibold text-[#1D242B]">Round settings</span>
          <CaretDown className={`w-4 h-4 text-[#455561] transition-transform ${settingsOpen ? 'rotate-180' : ''}`} weight="bold" />
        </button>

        {settingsOpen && (<>
        {/* Role banner — user can view but not edit */}
        {!isOrganiser && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold bg-[#F4F6F5] text-[#B8C7C0] border border-[#E1E6E4]" data-testid="view-only-banner">
            <Lock className="w-4 h-4 flex-shrink-0" weight="bold" />
            <span>View only — organiser access required to edit</span>
          </div>
        )}
        {/* Owner lock banner — organiser only */}
        {isOrganiser && hasScores && (
          <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold ${
            scoredLocked
              ? 'bg-[#FFF3E0] text-[#E59000] border border-[#FFE0B2]'
              : 'bg-[#E8F5E9] text-[#2E7D32] border border-[#C8E6C9]'
          }`} data-testid="owner-lock-banner">
            {scoredLocked ? (
              <>
                <Lock className="w-4 h-4 flex-shrink-0" weight="bold" />
                <span className="flex-1">Master password required — round has scores</span>
                <button
                  onClick={() => setOwnerDialogOpen(true)}
                  className="ml-auto px-3 py-1 text-xs font-bold bg-[#E59000] text-white rounded-lg hover:bg-[#D48400] transition-colors whitespace-nowrap"
                  data-testid="unlock-owner-btn"
                >
                  Unlock (owner)
                </button>
              </>
            ) : (
              <>
                <LockOpen className="w-4 h-4 flex-shrink-0" weight="bold" />
                <span>Unlocked (owner) — structural changes allowed</span>
              </>
            )}
          </div>
        )}
        <OwnerPasswordDialog
          open={ownerDialogOpen}
          onOpenChange={setOwnerDialogOpen}
          onSuccess={(token) => { setOwnerDialogOpen(false); setRoundUnlockToken(code, roundId, token); setOwnerUnlocked(true); }}
          description="This round has scores recorded. Enter the master password to unlock structural changes."
          tripCode={code}
        />
        {/* Course card */}
        <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl" data-testid="course-info">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-[#1D242B]">Course</h3>
            <Button size="sm" variant="ghost" onClick={() => navigate(`/trip/${code}/round/${roundId}/course`)}
              disabled={locked}
              className="text-[#1B4332] text-sm h-8 disabled:opacity-40" data-testid="change-course-btn"><Pencil className="w-4 h-4 mr-1" /> Change</Button>
          </div>
          <p className="text-base font-semibold text-[#1D242B]">{round.course?.name || 'Not selected'}</p>
          {round.course?.tee_name && <Badge className="bg-[#E8E5DB] text-[#1B4332] text-xs mt-1">{round.course.tee_name}</Badge>}
          <div className="flex gap-4 mt-2 text-xs text-[#455561]">
            <span>Par {round.course?.par}</span>
            <span>CR {round.course?.course_rating}</span>
            <span>Slope {round.course?.slope_rating}</span>
          </div>
        </Card>

        {/* Format + Team Scoring */}
        {(() => {
          const fmt = roundFormat(round);
          const ets = effectiveTeamScoring(trip, round);
          const tso = round.team_scoring_override;
          const isScramble = (round.scoring_mode || 'individual') === 'texas_scramble';

          const handleFormatChange = async (newFmt) => {
            if (locked) return;
            const fields = { format: newFmt };
            if (newFmt === 'nsp' && ets) {
              fields.team_scoring_override = false;
              setFormatNote('Team scoring turned off — NSP is individual only.');
              setTimeout(() => setFormatNote(''), 5000);
            }
            try {
              const res = await patchRound(code, roundId, fields);
              setTrip(res.data);
              const r = res.data.rounds?.find(r => r.id === roundId);
              if (r) setRound(r);
            } catch (e) {
              if (e.response?.status === 403) setFormatNote('Organiser access required');
            }
          };

          const handleTeamOverride = async (val) => {
            if (locked) return;
            const fields = { team_scoring_override: val };
            try {
              const res = await patchRound(code, roundId, fields);
              setTrip(res.data);
              const r = res.data.rounds?.find(r => r.id === roundId);
              if (r) setRound(r);
            } catch (e) {
              if (e.response?.status === 400) setFormatNote(e.response?.data?.detail || 'Invalid');
              if (e.response?.status === 403) setFormatNote('Organiser access required');
            }
          };

          return (
            <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl space-y-4" data-testid="format-section">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-[#1D242B]">Scoring Format</h3>
              </div>

              {/* Format selector */}
              <div className="flex gap-2" data-testid="format-selector">
                {[
                  { val: 'stableford', label: 'Stableford', desc: 'Points per hole, highest total wins.' },
                  { val: 'nsp', label: 'Net Stroke Play', desc: 'Gross strokes − handicap. Lowest net wins. Individual only.' },
                ].map(opt => (
                  <button key={opt.val} onClick={() => handleFormatChange(opt.val)} disabled={locked}
                    className={`flex-1 p-3 rounded-xl border-2 text-left transition-all ${
                      fmt === opt.val ? 'border-[#1B4332] bg-[#E8E5DB]' : 'border-[#E1E6E4] bg-white'
                    } ${locked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:border-[#1B4332]/50'}`}
                    data-testid={`format-${opt.val}`}>
                    <p className={`text-sm font-semibold ${fmt === opt.val ? 'text-[#1B4332]' : 'text-[#1D242B]'}`}>{opt.label}</p>
                    <p className="text-[10px] text-[#455561] mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>

              {/* Team scoring override — hidden on scramble (team-based by definition) */}
              {!isScramble && (
              <div>
                <label className="text-sm font-semibold text-[#455561] mb-2 block">Team Scoring</label>
                {fmt === 'nsp' ? (
                  <p className="text-xs text-[#E59000] bg-[#FFF8E1] rounded-lg px-3 py-2" data-testid="nsp-team-warning">
                    Net Stroke Play is an individual format — team scoring not applicable.
                  </p>
                ) : (
                  <div className="flex gap-2" data-testid="team-scoring-selector">
                    <button onClick={() => handleTeamOverride(null)} disabled={locked}
                      className={`flex-1 h-10 rounded-lg text-sm font-semibold transition-all ${
                        tso == null ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561]'
                      } ${locked ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid="team-default">
                      Trip default ({trip?.team_scoring ? 'On' : 'Off'})
                    </button>
                    <button onClick={() => handleTeamOverride(true)} disabled={locked}
                      className={`flex-1 h-10 rounded-lg text-sm font-semibold transition-all ${
                        tso === true ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561]'
                      } ${locked ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid="team-on">
                      On
                    </button>
                    <button onClick={() => handleTeamOverride(false)} disabled={locked}
                      className={`flex-1 h-10 rounded-lg text-sm font-semibold transition-all ${
                        tso === false ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561]'
                      } ${locked ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid="team-off">
                      Off
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* Team counting rule — only visible when team scoring is ON and format is Stableford */}
              {(() => {
                const teamOn = effectiveTeamScoring(trip, round);
                if (!teamOn || fmt === 'nsp') return null;
                const ts = round.team_size || 4;
                const tc = round.team_counting;
                const defaultP3 = 'all';
                const defaultLong = countingScoresOnLongHoles(ts);
                const currentP3 = tc?.par3 ?? defaultP3;
                const currentP4 = tc?.par4 ?? defaultLong;
                const currentP5 = tc?.par5 ?? defaultLong;
                const parOptions = (parKey, current) => {
                  const opts = [];
                  for (let i = 1; i <= ts; i++) opts.push({ label: String(i), value: i });
                  opts.push({ label: 'All', value: 'all' });
                  return opts;
                };
                const handleCountingChange = async (parKey, val) => {
                  const base = tc || { par3: defaultP3, par4: defaultLong, par5: defaultLong };
                  const updated = { ...base, [parKey]: val };
                  try {
                    const res = await patchRound(code, round.id, { team_counting: updated });
                    setRound(res.data.rounds?.find(r => r.id === round.id) || round);
                    toast.success('Counting rule updated');
                  } catch {
                    toast.error('Failed to update counting rule');
                  }
                };
                return (
                  <div data-testid="team-counting-section">
                    <label className="text-sm font-semibold text-[#455561] mb-2 block">Scores Counting</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[{ key: 'par3', label: 'Par 3', current: currentP3 },
                        { key: 'par4', label: 'Par 4', current: currentP4 },
                        { key: 'par5', label: 'Par 5', current: currentP5 }].map(({ key, label, current }) => (
                        <div key={key}>
                          <p className="text-xs text-[#455561] mb-1 text-center font-medium">{label}</p>
                          <Select
                            value={String(current)}
                            onValueChange={(v) => handleCountingChange(key, v === 'all' ? 'all' : parseInt(v, 10))}
                            disabled={scoredLocked}
                          >
                            <SelectTrigger className={`h-10 rounded-lg text-sm font-semibold text-center ${scoredLocked ? 'opacity-40' : ''}`} data-testid={`counting-${key}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {parOptions(key, current).map(opt => (
                                <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-[#B8C7C0] mt-1.5">How many player scores count toward the team total on each par.</p>
                  </div>
                );
              })()}

              {formatNote && <p className="text-xs text-[#E59000]" data-testid="format-note">{formatNote}</p>}
            </Card>
          );
        })()}

        {/* ── Scoring Mode (Individual / Texas Scramble) ── */}
        {(() => {
          const sm = round.scoring_mode || 'individual';
          const isScramble = sm === 'texas_scramble';
          const sc = round.scramble_config;
          const ts = round.team_size || 4;
          const sizeKey = ts === 2 ? 'pairs' : ts === 3 ? 'trios' : 'quads';
          const maxDrives = Math.floor(18 / ts);
          const currentAllowances = sc?.allowances?.[sizeKey] ?? (defaultScrambleConfig(ts).allowances[sizeKey]);
          const currentRounding = sc?.rounding ?? 'total';
          const currentMinDrives = sc?.min_drives ?? defaultMinDrives(ts);

          const handleScoringModeChange = async (mode) => {
            if (locked) return;
            const fields = { scoring_mode: mode };
            if (mode === 'texas_scramble' && !sc) {
              fields.scramble_config = defaultScrambleConfig(ts);
            }
            try {
              const res = await patchRound(code, roundId, fields);
              setTrip(res.data);
              const r = res.data.rounds?.find(r => r.id === roundId);
              if (r) setRound(r);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Failed to change scoring mode');
            }
          };

          const patchConfig = async (updates) => {
            if (locked) return;
            const merged = { ...(sc || defaultScrambleConfig(ts)), ...updates };
            if (updates.allowances) merged.allowances = { ...(sc?.allowances || defaultScrambleConfig(ts).allowances), ...updates.allowances };
            try {
              const res = await patchRound(code, roundId, { scramble_config: merged });
              setTrip(res.data);
              const r = res.data.rounds?.find(r => r.id === roundId);
              if (r) setRound(r);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Failed to update config');
            }
          };

          const posLabels = ts === 2 ? ['Lower HC', 'Higher HC'] : ts === 3 ? ['Lowest HC', '2nd', 'Highest HC'] : ['Lowest HC', '2nd', '3rd', 'Highest HC'];
          const teams = round.teams || [];

          return (
            <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl space-y-4" data-testid="scoring-mode-section">
              <h3 className="font-bold text-[#1D242B]">Scoring Mode</h3>

              {/* Mode toggle */}
              <div className="flex gap-2" data-testid="scoring-mode-selector">
                {[
                  { val: 'individual', label: 'Individual', desc: 'Each player scores their own ball.' },
                  { val: 'texas_scramble', label: 'Texas Scramble', desc: 'Team picks best shot each hole.' },
                ].map(opt => (
                  <button key={opt.val} onClick={() => handleScoringModeChange(opt.val)} disabled={locked}
                    className={`flex-1 p-3 rounded-xl border-2 text-left transition-all ${
                      sm === opt.val ? 'border-[#1B4332] bg-[#E8E5DB]' : 'border-[#E1E6E4] bg-white'
                    } ${locked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:border-[#1B4332]/50'}`}
                    data-testid={`mode-${opt.val}`}>
                    <p className={`text-sm font-semibold ${sm === opt.val ? 'text-[#1B4332]' : 'text-[#1D242B]'}`}>{opt.label}</p>
                    <p className="text-[10px] text-[#455561] mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
              {locked && hasScores && <p className="text-[10px] text-[#E59000]">Locked — scores already entered</p>}

              {/* Scramble Config editor */}
              {isScramble && (
                <div className="space-y-4 pt-1" data-testid="scramble-config">
                  {/* Allowances */}
                  <div>
                    <label className="text-sm font-semibold text-[#455561] mb-2 block">
                      Allowances ({ts === 2 ? 'Pairs' : ts === 3 ? 'Trios' : 'Quads'})
                    </label>
                    <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${ts}, 1fr)` }}>
                      {currentAllowances.map((pct, i) => (
                        <div key={i}>
                          <p className="text-[10px] text-[#455561] mb-1 text-center font-medium">{posLabels[i]}</p>
                          <Input
                            type="number" min={0} max={100} value={pct}
                            disabled={locked}
                            onChange={(e) => {
                              const v = parseInt(e.target.value) || 0;
                              if (v < 0 || v > 100) return;
                              const updated = [...currentAllowances];
                              updated[i] = v;
                              patchConfig({ allowances: { [sizeKey]: updated } });
                            }}
                            className="h-10 rounded-lg text-sm text-center font-semibold disabled:opacity-40"
                            data-testid={`allowance-${i}`}
                          />
                          <p className="text-[10px] text-[#B8C7C0] text-center mt-0.5">{pct}%</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Rounding */}
                  <div>
                    <label className="text-sm font-semibold text-[#455561] mb-2 block">Rounding</label>
                    <div className="flex gap-2" data-testid="rounding-selector">
                      {[
                        { val: 'total', label: 'Round the total', desc: 'Sum shares, round once.' },
                        { val: 'each', label: 'Round each share', desc: 'Round each player share, then sum.' },
                      ].map(opt => (
                        <button key={opt.val} onClick={() => patchConfig({ rounding: opt.val })} disabled={locked}
                          className={`flex-1 p-2.5 rounded-xl border-2 text-left transition-all ${
                            currentRounding === opt.val ? 'border-[#1B4332] bg-[#E8E5DB]' : 'border-[#E1E6E4] bg-white'
                          } ${locked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:border-[#1B4332]/50'}`}
                          data-testid={`rounding-${opt.val}`}>
                          <p className={`text-xs font-semibold ${currentRounding === opt.val ? 'text-[#1B4332]' : 'text-[#1D242B]'}`}>{opt.label}</p>
                          <p className="text-[9px] text-[#455561] mt-0.5">{opt.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Min drives */}
                  <div>
                    <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Minimum Drives Per Player</label>
                    <Input
                      type="number" min={0} max={maxDrives}
                      value={currentMinDrives}
                      disabled={locked}
                      onChange={(e) => {
                        const v = parseInt(e.target.value);
                        if (isNaN(v) || v < 0) return;
                        if (v > maxDrives) { toast.error(`Cannot exceed ${maxDrives} for ${ts}-ball teams`); return; }
                        patchConfig({ min_drives: v });
                      }}
                      className="h-10 rounded-lg text-sm w-24 disabled:opacity-40"
                      data-testid="min-drives-input"
                    />
                    <p className="text-[10px] text-[#B8C7C0] mt-1">Minimum tee shots each player must contribute. Cannot exceed {maxDrives}.</p>
                  </div>

                  {/* Live team-handicap preview */}
                  <div data-testid="team-ph-preview">
                    <label className="text-sm font-semibold text-[#455561] mb-2 block">Team Handicaps</label>
                    {teams.length === 0 ? (
                      <p className="text-xs text-[#B8C7C0] italic">Set up teams to see handicaps</p>
                    ) : (
                      <div className="space-y-2">
                        {teams.map((teamPids, idx) => {
                          const teamPlayers = teamPids.map(pid => trip?.players?.find(p => p.id === pid)).filter(Boolean);
                          const ph = computeTeamPH(round, idx, trip);
                          const sizeMismatch = teamPlayers.length !== ts && teamPlayers.length > 0;
                          return (
                            <div key={idx} className="rounded-xl border border-[#E1E6E4] overflow-hidden" data-testid={`team-ph-card-${idx}`}>
                              <div className="flex items-center justify-between px-3 py-2 bg-[#F4F6F5]">
                                <span className="text-xs font-semibold text-[#1D242B]">{groupDisplayName(round, idx)}</span>
                                <span className="text-xs font-bold text-[#1B4332]">Team PH {ph}</span>
                              </div>
                              <div className="px-3 py-1.5">
                                {teamPlayers.map(p => {
                                  const playerPh = getAllowedPH(p, effectiveCourseForPlayer(round, p.id), round);
                                  return (
                                    <div key={p.id} className="text-[11px] text-[#455561] py-0.5 break-words">{p.name} — PH {formatPlayingHandicap(playerPh)}</div>
                                  );
                                })}
                                {sizeMismatch && (
                                  <div className="text-[10px] text-[#D32F2F] font-semibold mt-1" data-testid={`team-size-warn-${idx}`}>
                                    Team has {teamPlayers.length} players; expected {ts} for this round
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          );
        })()}

        {/* Player tees */}
        {courseTees.length > 1 && players.length > 0 && (
          <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl" data-testid="player-tees-section">
            <h3 className="font-bold text-[#1D242B] mb-3">Player Tees</h3>
            <p className="text-xs text-[#455561] mb-3">Override individual tees. Default: {round.course?.tee_name || 'course default'}</p>
            <div className="space-y-2">
              {players.map(player => {
                const currentTee = playerTees[player.id] || '__default__';
                const effectiveCourse = effectiveCourseForPlayer(round, player.id);
                const ph = getAllowedPH(player, effectiveCourse, round);
                return (
                  <div key={player.id} className="flex flex-col gap-0.5" data-testid={`player-tee-row-${player.id}`}>
                    <div className="flex items-center gap-2 min-h-[44px]">
                      <span className="text-sm text-[#1D242B] font-medium truncate flex-1 min-w-0">{player.name}</span>
                      <Badge className="bg-[#1B4332] text-white text-[10px] font-bold px-1.5 flex-shrink-0">PH {formatPlayingHandicap(ph)}</Badge>
                      <Select value={currentTee} onValueChange={(v) => handlePlayerTeeChange(player.id, v)} disabled={locked}>
                        <SelectTrigger className="h-9 w-[140px] text-xs rounded-lg border-[#E1E6E4] flex-shrink-0 disabled:opacity-40" data-testid={`player-tee-select-${player.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__default__">Round default</SelectItem>
                          {courseTees.map(tee => (
                            <SelectItem key={tee.name} value={tee.name}>{tee.name}{tee.course_rating != null && tee.slope_rating != null ? ` · CR ${tee.course_rating} · S ${tee.slope_rating}` : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {player.handicap_mode === 'ph' && (
                      <p className="text-[10px] text-[#E59000] ml-0.5 -mt-0.5 mb-1" data-testid={`manual-ph-note-${player.id}`}>PH manually set — tee change won't affect handicap</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Round settings */}
        <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl space-y-4" data-testid="round-settings">
          <h3 className="font-bold text-[#1D242B]">Settings</h3>
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Round Name</label>
            <Input value={label} onChange={(e) => !locked && setLabel(e.target.value)}
              onBlur={() => !locked && label !== round.label && save({ label })}
              disabled={locked}
              className="h-14 rounded-xl text-base disabled:opacity-40" data-testid="round-label-input" />
          </div>
          <div className="flex items-center justify-between min-h-[56px]">
            <div><p className="font-semibold text-sm text-[#1D242B]">Freeze Leaderboard</p><p className="text-xs text-[#455561]">Hide scores past a set hole until organiser reveals</p></div>
            <Switch checked={freezeEnabled} disabled={locked} onCheckedChange={(v) => { if (!locked) { setFreezeEnabled(v); save({ freeze_enabled: v }); } }} data-testid="freeze-enabled-switch" />
          </div>
          {freezeEnabled && (
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Freeze Leaderboard At</label>
            <Select value={String(revealHole)} onValueChange={(v) => { if (locked) return; setRevealHole(parseInt(v)); save({ reveal_hole: parseInt(v) }); }} disabled={locked}>
              <SelectTrigger className="h-14 rounded-xl text-base disabled:opacity-40" data-testid="reveal-hole-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">From the start</SelectItem>
                {[12, 13, 14, 15, 16].map(h => <SelectItem key={h} value={String(h)}>After hole {h}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#B8C7C0] mt-1">{revealHole === 0 ? 'All scores hidden until reveal' : 'Leaderboard freezes after this hole until reveal'}</p>
          </div>
          )}

          {/* Use Divisions toggle — only when trip has divisions, hidden on Scramble */}
          {trip?.divisions_enabled && (trip?.divisions?.length || 0) > 0 && (round?.scoring_mode || 'individual') !== 'texas_scramble' && (
          <div data-testid="use-divisions-section">
            <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Use Divisions</label>
            <div className="flex items-center gap-3">
              <button
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${round?.use_divisions !== false ? 'bg-[#1B4332]' : 'bg-[#B8C7C0]'}`}
                onClick={() => save({ use_divisions: round?.use_divisions === false ? true : false })}
                data-testid="use-divisions-toggle"
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${round?.use_divisions !== false ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-[#455561]">{round?.use_divisions !== false ? 'On' : 'Off'}</span>
            </div>
            <p className="text-xs text-[#B8C7C0] mt-1">Split leaderboard by handicap division</p>
          </div>
          )}


          {/* Handicap Basis — NSP only, hidden for Scramble */}
          {(round?.scoring_mode || 'individual') !== 'texas_scramble' && roundFormat(round) === 'nsp' && (
          <div data-testid="handicap-basis-section">
            <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Handicap Basis</label>
            {hasScores && !ownerUnlocked ? (
              <div>
                <p className="text-sm text-[#1D242B] font-semibold mb-1">{{ entered: 'Entered Handicap', system36: 'System 36', lower_of: 'Lower Of' }[round?.handicap_basis || 'entered']}</p>
                <p className="text-xs text-[#B8C7C0]">Locked once scoring has started</p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 mb-2">
                  {[
                    { value: 'entered', label: 'Entered Handicap' },
                    { value: 'system36', label: 'System 36' },
                    { value: 'lower_of', label: 'Lower Of' },
                  ].map(opt => (
                    <button key={opt.value}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                        (round?.handicap_basis || 'entered') === opt.value
                          ? 'bg-[#1B4332] text-white'
                          : 'bg-[#F4F6F5] text-[#455561] hover:bg-[#E8E5DB]'
                      }`}
                      disabled={locked}
                      data-testid={`basis-chip-${opt.value}`}
                      onClick={() => { if (!locked) save({ handicap_basis: opt.value }); }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#B8C7C0] mt-1">
                  {(round?.handicap_basis || 'entered') === 'entered' && 'Uses each player\'s entered playing handicap'}
                  {round?.handicap_basis === 'system36' && 'Handicap calculated from scores: 36 minus points earned'}
                  {round?.handicap_basis === 'lower_of' && 'Uses whichever is lower: entered (after allowance) or System 36'}
                </p>
              </>
            )}
          </div>
          )}

          {/* S36 Max Handicap — shown when basis is system36 or lower_of */}
          {(round?.scoring_mode || 'individual') !== 'texas_scramble' && roundFormat(round) === 'nsp' && (round?.handicap_basis === 'system36' || round?.handicap_basis === 'lower_of') && (
          <div data-testid="s36-max-handicap-section">
            <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Maximum System 36 Handicap</label>
            {hasScores && !ownerUnlocked ? (
              <div>
                <p className="text-sm text-[#1D242B] font-semibold mb-1">{round?.s36_max_handicap != null ? round.s36_max_handicap : 'None'}</p>
                <p className="text-xs text-[#B8C7C0]">Locked once scoring has started</p>
              </div>
            ) : (
              <>
                <Input type="number" min={0} max={54} step={1}
                  value={round?.s36_max_handicap ?? ''}
                  placeholder="No maximum"
                  disabled={locked}
                  className="h-10 rounded-xl text-sm w-32 disabled:opacity-40"
                  data-testid="s36-max-input"
                  onChange={(e) => {
                    if (locked) return;
                    const raw = e.target.value.trim();
                    if (raw === '') {
                      save({ s36_max_handicap: null });
                    } else {
                      const v = parseInt(raw);
                      if (Number.isFinite(v) && v >= 0 && v <= 54 && Math.floor(v) === v) {
                        save({ s36_max_handicap: v });
                      }
                    }
                  }} />
                <p className="text-xs text-[#B8C7C0] mt-1">Leave blank for no maximum. 0–54.</p>
              </>
            )}
          </div>
          )}

          {/* Handicap Allowance — hidden for Scramble and hidden for pure System 36 */}
          {(round?.scoring_mode || 'individual') !== 'texas_scramble' && round?.handicap_basis !== 'system36' && (
          <div data-testid="handicap-allowance-section">
            <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Handicap Allowance</label>
            {hasScores && !ownerUnlocked ? (
              <div>
                <p className="text-sm text-[#1D242B] font-semibold mb-1">{round?.handicap_allowance_pct ?? 100}%</p>
                <p className="text-xs text-[#B8C7C0]">Locked once scoring has started</p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 mb-2">
                  {[100, 95, 90, 85].map(pct => (
                    <button key={pct}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                        (round?.handicap_allowance_pct ?? 100) === pct
                          ? 'bg-[#1B4332] text-white'
                          : 'bg-[#F4F6F5] text-[#455561] hover:bg-[#E8E5DB]'
                      }`}
                      disabled={locked}
                      data-testid={`allowance-chip-${pct}`}
                      onClick={() => { if (!locked) save({ handicap_allowance_pct: pct }); }}>
                      {pct}%
                    </button>
                  ))}
                </div>
                <Input type="number" min={0} max={100} step={1}
                  value={round?.handicap_allowance_pct ?? 100}
                  disabled={locked}
                  className="h-10 rounded-xl text-sm w-24 disabled:opacity-40"
                  data-testid="allowance-input"
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    if (!locked && Number.isFinite(v) && v >= 0 && v <= 100 && Math.floor(v) === v) {
                      save({ handicap_allowance_pct: v });
                    }
                  }} />
                <p className="text-xs text-[#B8C7C0] mt-1">% of each player's playing handicap used for scoring</p>
              </>
            )}
          </div>
          )}

          <div className="flex items-center justify-between min-h-[56px]">
            <div><p className="font-semibold text-sm text-[#1D242B]">Final Round</p><p className="text-xs text-[#455561]">Marks this as the closing round</p></div>
            <Switch checked={isFinal} disabled={locked} onCheckedChange={(v) => { if (!locked) { setIsFinal(v); save({ is_final: v }); } }} data-testid="final-round-switch" />
          </div>
        </Card>

        {/* Tee times */}
        <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl space-y-4" data-testid="tee-times-settings">
          <h3 className="font-bold text-[#1D242B]">Tee Times</h3>
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Start Type</label>
            <Select value={startType} disabled={locked} onValueChange={(v) => {
              if (locked) return;
              setStartType(v);
              const fields = { start_type: v };
              if (v === 'split_tee') {
                fields.split_tee_holes = splitTeeHoles;
                fields.split_tee_first_times = splitTeeTimes.map(t => t || null);
                // Default alternating group_start_holes
                const numGroups = round.teams?.length || 0;
                if (numGroups > 0) {
                  fields.group_start_holes = Array.from({length: numGroups}, (_, i) => splitTeeHoles[i % 2]);
                }
              }
              save(fields);
            }}>
              <SelectTrigger className="h-14 rounded-xl text-base disabled:opacity-40" data-testid="start-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (Sequential)</SelectItem>
                <SelectItem value="shotgun">Shotgun Start</SelectItem>
                <SelectItem value="split_tee">Split Tee</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Starting Hole for Standard rounds */}
          {startType === 'standard' && (
            <div>
              <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Starting Hole</label>
              <Select value={String(startingHole)} disabled={locked} onValueChange={(v) => {
                if (locked) return;
                const h = parseInt(v);
                setStartingHole(h);
                save({ starting_hole: h });
              }}>
                <SelectTrigger className="h-14 rounded-xl text-base disabled:opacity-40" data-testid="starting-hole-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({length: 18}, (_, i) => i + 1).map(h => (
                    <SelectItem key={h} value={String(h)}>Hole {h}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {startingHole > 1 && <p className="text-xs text-[#B8C7C0] mt-1">Sequence: {Array.from({length: 18}, (_, i) => ((startingHole - 1 + i) % 18) + 1).join(', ')}</p>}
            </div>
          )}

          {/* Split Tee configuration */}
          {startType === 'split_tee' && (
            <div className="space-y-3 bg-[#F4F6F5] rounded-xl p-3">
              <p className="text-xs text-[#455561] font-semibold">Two starting holes with independent tee times</p>
              <div className="grid grid-cols-2 gap-3">
                {[0, 1].map(idx => (
                  <div key={idx} className="space-y-2">
                    <label className="text-xs font-semibold text-[#1D242B]">Starting Hole {idx === 0 ? 'A' : 'B'}</label>
                    <Select value={String(splitTeeHoles[idx])} onValueChange={(v) => {
                      const newHoles = [...splitTeeHoles];
                      newHoles[idx] = parseInt(v);
                      setSplitTeeHoles(newHoles);
                      const fields = { split_tee_holes: newHoles };
                      // Re-assign groups to alternate between new holes
                      const numGroups = round.teams?.length || 0;
                      if (numGroups > 0) {
                        fields.group_start_holes = Array.from({length: numGroups}, (_, i) => newHoles[i % 2]);
                      }
                      save(fields);
                    }}>
                      <SelectTrigger className="h-10 rounded-lg text-sm" data-testid={`split-hole-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({length:18}, (_,i) => i+1)
                          .filter(h => h !== splitTeeHoles[1 - idx])
                          .map(h => <SelectItem key={h} value={String(h)}>Hole {h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <label className="text-xs font-semibold text-[#455561]">Tee Time {idx === 0 ? 'A' : 'B'}</label>
                    <Input type="time" value={splitTeeTimes[idx]}
                      onChange={(e) => {
                        const newTimes = [...splitTeeTimes];
                        newTimes[idx] = e.target.value;
                        setSplitTeeTimes(newTimes);
                      }}
                      onBlur={() => save({ split_tee_first_times: splitTeeTimes.map(t => t || null) })}
                      className="h-10 rounded-lg text-sm" data-testid={`split-time-${idx}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tee time for standard / shotgun */}
          {startType !== 'split_tee' && (
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 min-w-0">
              <label className="text-sm font-semibold text-[#455561] mb-1.5 block">First Tee Time</label>
              <div className="relative min-w-0">
                <Input type="time" value={firstTeeTime} onChange={(e) => !locked && setFirstTeeTime(e.target.value)}
                  onBlur={() => !locked && save({ first_tee_time: firstTeeTime || null })}
                  disabled={locked}
                  className="h-14 rounded-xl w-full max-w-full box-border disabled:opacity-40"
                  style={{ fontSize: '16px', WebkitAppearance: 'none', appearance: 'none', minWidth: 0 }}
                  data-testid="first-tee-time" />
                {!firstTeeTime && (
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B8C7C0] text-sm pointer-events-none" data-testid="tee-time-placeholder">Tap to set</span>
                )}
              </div>
            </div>
            {!isShotgunRound(round) && (
              <div className="flex-1">
                <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Interval</label>
                <div className="flex items-center gap-2">
                  <Input type="number" min={5} max={15} value={teeGapRaw}
                    onChange={(e) => { if (!locked) { setTeeGapRaw(e.target.value); clearTeeGapError(); } }}
                    onBlur={() => {
                      if (locked) return;
                      const n = parseInt(teeGapRaw, 10);
                      if (isNaN(n) || n < 5 || n > 15) {
                        setTeeGapRaw(String(teeGap));
                        showTeeGapError('Interval must be 5 to 15 minutes');
                      } else {
                        setTeeGap(n);
                        setTeeGapRaw(String(n));
                        clearTeeGapError();
                        save({ tee_gap_minutes: n });
                      }
                    }}
                    disabled={locked}
                    className="h-14 w-20 rounded-xl text-center text-base font-semibold"
                    data-testid="tee-gap-input" />
                  <span className="text-sm text-[#455561]">min</span>
                </div>
                {teeGapError && <p className="text-xs text-[#D32F2F] mt-1" data-testid="tee-gap-error">{teeGapError}</p>}
              </div>
            )}
          </div>
          )}

          {/* Shared gap for split tee */}
          {startType === 'split_tee' && (
            <div>
              <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Gap Between Groups</label>
              <div className="flex items-center gap-2">
                <Input type="number" min={5} max={15} value={teeGapRaw}
                  onChange={(e) => { setTeeGapRaw(e.target.value); clearTeeGapError(); }}
                  onBlur={() => {
                    const n = parseInt(teeGapRaw, 10);
                    if (isNaN(n) || n < 5 || n > 15) {
                      setTeeGapRaw(String(teeGap));
                      showTeeGapError('Interval must be 5 to 15 minutes');
                    } else {
                      setTeeGap(n);
                      setTeeGapRaw(String(n));
                      clearTeeGapError();
                      save({ tee_gap_minutes: n });
                    }
                  }}
                  className="h-14 w-20 rounded-xl text-center text-base font-semibold"
                  data-testid="tee-gap-input-split" />
                <span className="text-sm text-[#455561]">min</span>
              </div>
              {teeGapError && <p className="text-xs text-[#D32F2F] mt-1" data-testid="tee-gap-error">{teeGapError}</p>}
            </div>
          )}

          {/* Group tee times — show when first tee time is set and groups exist */}
          {firstTeeTime && round?.teams?.length > 0 && startType === 'standard' && (
            <div className="mt-4">
              <label className="text-sm font-semibold text-[#455561] mb-2 block">Group Tee Times</label>
              <div className="space-y-1.5">
                {round.teams.map((team, gi) => {
                  const computed = teeTimeForGroup(round, gi);
                  const isManual = round.group_tee_time_manual?.[gi] === true;
                  return (
                    <div key={gi} className="flex items-center gap-2 h-9" data-testid={`group-tee-row-${gi}`}>
                      <span className="text-xs font-semibold text-[#455561] w-16 flex-shrink-0">
                        {round.group_names?.[gi] || `Group ${gi + 1}`}
                      </span>
                      <Input type="time" value={round.group_tee_times?.[gi] || computed || ''}
                        onChange={(e) => {
                          if (locked) return;
                          const newTimes = [...(round.group_tee_times || Array(round.teams.length).fill(null))];
                          while (newTimes.length < round.teams.length) newTimes.push(null);
                          newTimes[gi] = e.target.value || null;
                          const newManual = [...(round.group_tee_time_manual || Array(round.teams.length).fill(false))];
                          while (newManual.length < round.teams.length) newManual.push(false);
                          newManual[gi] = true;
                          save({ group_tee_times: newTimes, group_tee_time_manual: newManual });
                        }}
                        disabled={locked}
                        className="h-9 w-24 rounded-lg text-xs text-center"
                        data-testid={`group-tee-input-${gi}`} />
                      {isManual && (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#E59000] inline-block" />
                          <span className="text-[9px] text-[#E59000] font-semibold">edited</span>
                          <button onClick={() => {
                            if (locked) return;
                            const newTimes = [...(round.group_tee_times || [])];
                            while (newTimes.length <= gi) newTimes.push(null);
                            newTimes[gi] = null;
                            const newManual = [...(round.group_tee_time_manual || [])];
                            while (newManual.length <= gi) newManual.push(false);
                            newManual[gi] = false;
                            save({ group_tee_times: newTimes, group_tee_time_manual: newManual });
                          }} className="text-[9px] text-[#B8C7C0] hover:text-[#455561] underline ml-1"
                            data-testid={`group-tee-reset-${gi}`}>reset</button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
        <Button onClick={() => navigate(`/trip/${code}/round/${roundId}/teams`)}
          className="w-full h-12 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl text-sm font-semibold" data-testid="team-draw-btn">
          <Users className="w-4 h-4 mr-1.5" weight="bold" /> Team Draw
        </Button>
        </>
        )}
      </div>
    </div>
  );
}
